// Procedurally-synthesized UI sound effects via the Web Audio API - no audio
// files to bundle/load, which matters here since the game's CEF browser may
// have no internet access (same reasoning as the self-hosted fonts in
// main.scss). Ported from the "SkyMP RP Studio" mockup's lib/audio.ts,
// trimmed to the sounds Toast actually uses. `notify-success.mp3` is the one
// exception - a real bundled sound file the user asked for specifically for
// the "added someone" (success) toast, so it's a webpack asset import
// (hashed, self-hosted the same way as the fonts) rather than synthesized.
import notifySuccessUrl from '../sfx/notify-success.mp3';

const VOLUME_STORAGE_KEY = 'skymp5-esc-menu-notification-volume';
export const DEFAULT_NOTIFICATION_VOLUME = 0.3;

export const getStoredNotificationVolume = (): number => {
  try {
    const raw = window.localStorage.getItem(VOLUME_STORAGE_KEY);
    const parsed = raw ? parseFloat(raw) : DEFAULT_NOTIFICATION_VOLUME;
    if (!Number.isFinite(parsed)) return DEFAULT_NOTIFICATION_VOLUME;
    return Math.max(0, Math.min(1, parsed));
  } catch {
    return DEFAULT_NOTIFICATION_VOLUME;
  }
};

// Chromium (and this game's embedded CEF) blocks autoplay until the page has
// seen a real user gesture - a toast triggered by an incoming server packet
// has none in its call stack, so a cold `new Audio(url).play()` call from
// there is silently rejected. The dice roller (constructorComponents/chat/
// dices) never hits this because it plays its own sounds from a real button
// click. Fix: "unlock" audio the moment the player generates ANY real gesture
// (near-guaranteed almost immediately in-game - WASD, mouse look, etc. all
// count) by playing and instantly pausing a throwaway clip once; Chromium
// then allows subsequent non-gesture playback on this page for the rest of
// the session.
let audioUnlocked = false;
function unlockAudioOnFirstGesture(): void {
  if (audioUnlocked || typeof window === 'undefined') return;
  const unlock = () => {
    if (audioUnlocked) return;
    audioUnlocked = true;
    window.removeEventListener('keydown', unlock, true);
    window.removeEventListener('mousedown', unlock, true);
    try {
      const primer = new Audio(notifySuccessUrl);
      primer.volume = 0;
      primer.play().then(() => primer.pause()).catch(() => { /* still fine to try real playback later */ });
    } catch {
      /* ignore */
    }
  };
  window.addEventListener('keydown', unlock, true);
  window.addEventListener('mousedown', unlock, true);
}

class UiSoundEngine {
  private ctx: AudioContext | null = null;
  private volume: number = getStoredNotificationVolume();

  constructor() {
    unlockAudioOnFirstGesture();
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    try {
      window.localStorage.setItem(VOLUME_STORAGE_KEY, String(this.volume));
    } catch {
      /* ignore */
    }
  }

  private getContext(): AudioContext | null {
    if (!this.ctx && typeof window !== 'undefined') {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (AudioCtx) {
        this.ctx = new AudioCtx();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    return this.ctx;
  }

  // Real bundled sound (see the notify-success.mp3 import above), for the
  // "success" toast type (adding a member, a successful housing/faction
  // action, etc.) - plain HTMLAudioElement, same as the dice roller, once
  // audio is unlocked (see unlockAudioOnFirstGesture above). A new element
  // per call so overlapping toasts don't cut each other's playback off.
  playSuccessSample(): void {
    if (this.volume <= 0) return;
    const audio = new Audio(notifySuccessUrl);
    audio.volume = this.volume;
    audio.play().catch(() => { /* not yet unlocked - the next one will work */ });
  }

  // Generic UI click/notification blip.
  playClick(): void {
    if (this.volume <= 0) return;
    const ctx = this.getContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(140, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(30, ctx.currentTime + 0.06);

    gain.gain.setValueAtTime(0.18 * this.volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.06);
  }

  // Gold Septims clinking together.
  playGoldJingle(): void {
    if (this.volume <= 0) return;
    const ctx = this.getContext();
    if (!ctx) return;
    const freqs = [1800, 2400, 3100];
    freqs.forEach((freq, idx) => {
      setTimeout(() => {
        if (!ctx) return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq + (Math.random() * 80 - 40), ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(freq * 0.8, ctx.currentTime + 0.25);

        gain.gain.setValueAtTime(0.12 * this.volume, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start();
        osc.stop(ctx.currentTime + 0.25);
      }, idx * 45);
    });
  }

  // Ancient horn / quest resonance.
  playQuestFanfare(): void {
    if (this.volume <= 0) return;
    const ctx = this.getContext();
    if (!ctx) return;
    const notes = [146.83, 220.0, 293.66]; // D3, A3, D4
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, ctx.currentTime);

      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(600, ctx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.9);

      gain.gain.setValueAtTime(0.08 * this.volume, ctx.currentTime + i * 0.08);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.2);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);

      osc.start(ctx.currentTime + i * 0.08);
      osc.stop(ctx.currentTime + 1.2);
    });
  }
}

export const uiSounds = new UiSoundEngine();
